/**
 * D-EZ-107 / D-EZ-108 — WHO HE IS, AND WHO HE HAS HISTORY WITH (driver's card).
 *
 * Two boxes under the trophy case:
 *   • Personality — his temperament in plain words ("Short fuse", "Ice in his
 *     veins", "Fiercely loyal to Waddell Brothers"), where he came up, how his
 *     career has changed him, and his last word to the press, in his voice.
 *   • Rivals & friends — his rivals with their heat and the worst thing between
 *     them, his friends, the veteran who brought him along or the young men he
 *     is bringing along. Every name opens that man's card.
 *
 * Narrative only (D-EZ-104…111): derived from the league's history by hash and
 * fold; nothing here is stored and nothing here touches a race.
 */

import { useMemo } from 'react';
import type { HistorySeason } from '../../domain/history/index.ts';
import {
  factsForRace,
  peopleContext,
  personalityOf,
  quote,
  relationshipsOf,
  STATE_WORDS,
  type Quote,
} from '../../domain/people/index.ts';
import { BURNING, HeatMeter, whenOf } from './RivalryBoard.tsx';

export interface PeoplePanelProps {
  driverId: string;
  seasons: readonly HistorySeason[];
  onSelectDriver?: (driverId: string) => void;
}

/** His most recent quotable fact, in his voice, with the race it came from. */
function lastWord(ctx: ReturnType<typeof peopleContext>, driverId: string): { q: Quote; raceName: string; seasonNumber: number } | null {
  for (let i = ctx.seasons.length - 1; i >= 0; i--) {
    const s = ctx.seasons[i];
    const races = [...s.season.races].sort((a, b) => b.raceNumber - a.raceNumber);
    for (const run of races.slice(0, 12)) {
      if (!run.race.finishingOrder.some((f) => f.driverId === driverId)) continue;
      const mine = factsForRace(ctx, s.seasonNumber, run.raceNumber).filter((f) => f.driverId === driverId);
      for (const f of mine) {
        const q = quote(f.fact, driverId, ctx);
        if (q) return { q, raceName: run.scheduled.raceName, seasonNumber: s.seasonNumber };
      }
    }
    if (i < ctx.seasons.length - 1) break;
  }
  return null;
}

export default function PeoplePanel({ driverId, seasons, onSelectDriver }: PeoplePanelProps) {
  const ctx = useMemo(() => peopleContext({ archive: seasons }), [seasons]);
  const persona = useMemo(() => personalityOf(driverId, ctx), [ctx, driverId]);
  const rel = useMemo(() => relationshipsOf(ctx.book, driverId), [ctx, driverId]);
  const word = useMemo(() => lastWord(ctx, driverId), [ctx, driverId]);
  const rivals = rel.rivals.filter((r) => r.record.state !== 'dormant').slice(0, 5);
  const friends = rel.friends.filter((f) => f.bond.current).slice(0, 4);
  const link = (id: string, name: string) =>
    onSelectDriver ? (
      <button type="button" className="linkish" onClick={() => onSelectDriver(id)}>
        {name}
      </button>
    ) : (
      <span>{name}</span>
    );

  return (
    <div className="people">
      <section className="dbox people__box" aria-labelledby={`persona-${driverId}`}>
        <h3 id={`persona-${driverId}`}>Personality</h3>
        <ul className="people__traits" aria-label="Traits">
          {persona.traits.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
        {persona.backstory ? (
          <p className="people__story">{persona.backstory}</p>
        ) : (
          persona.real && <p className="people__note">How this league’s racing has shown him. The game writes no life story for a real driver.</p>
        )}
        {persona.shifts.length > 0 && (
          <>
            <h4>How his career has changed him</h4>
            <ul className="people__list">
              {persona.shifts.map((s, i) => (
                <li key={i}>{s.why}</li>
              ))}
            </ul>
          </>
        )}
        {word && (
          <figure className="people__quote">
            <blockquote>
              <p>“{word.q.text}”</p>
            </blockquote>
            <figcaption>
              After the {word.raceName}, season {word.seasonNumber}
            </figcaption>
          </figure>
        )}
      </section>

      <section className="dbox people__box" aria-labelledby={`rivals-${driverId}`}>
        <h3 id={`rivals-${driverId}`}>Rivals &amp; friends</h3>
        {rivals.length === 0 && friends.length === 0 && !rel.mentor && rel.proteges.length === 0 ? (
          <p className="people__note">Nobody has it in for him, and nobody has taken him under a wing — yet.</p>
        ) : null}
        {rivals.length > 0 && (
          <ul className="people__rivals">
            {rivals.map((r) => (
              <li key={r.other}>
                <div className="people__rivalhead">
                  {link(r.other, r.otherName)}
                  <span className={`rival__state rival__state--${r.record.state}`}>{STATE_WORDS[r.record.state]}</span>
                </div>
                {BURNING.has(r.record.state) && <HeatMeter heat={r.record.heat} state={r.record.state} />}
                {r.record.worst && (
                  <p className="people__worst">
                    {r.record.worst.text} <span className="rival__when">{whenOf(r.record.worst)}</span>
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
        {(friends.length > 0 || rel.mentor || rel.proteges.length > 0) && (
          <>
            <h4>On his side</h4>
            <ul className="people__list">
              {rel.mentor && <li>Brought along by {link(rel.mentor.id, rel.mentor.name)}</li>}
              {rel.proteges.map((p) => (
                <li key={p.id}>Bringing along {link(p.id, p.name)}</li>
              ))}
              {friends.map((f) => (
                <li key={f.other}>
                  {link(f.other, f.otherName)}{' '}
                  <span className="rival__when">
                    — {f.bond.kind === 'friends' ? 'teammate and friend' : `teammates ${f.bond.seasonsTogether.length} seasons`}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}
